import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Calendar,
  Clock,
  Plus,
  X,
  Sparkles,
  Bell,
  AlertTriangle,
  CheckCircle2,
  Trash2,
  Loader2,
} from "lucide-react";
import { formatHour } from "./utils";

/**
 * Meetings card — Today / Upcoming-7-days toggle, "Add" dialog with prep
 * items + reminders + AI-generated prep notes, and per-meeting delete.
 *
 * Parent owns the meetings array and handles persistence via the
 * onAddMeeting / onDeleteMeeting / onUpdateMeeting callbacks (when
 * provided). Falls back to local-only state when no callbacks given.
 */
const MeetingPlanner = ({ meetings, setMeetings, onAddMeeting, onDeleteMeeting, onUpdateMeeting }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [showUpcoming, setShowUpcoming] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [upcomingMeetings, setUpcomingMeetings] = useState([]);
  const [newMeeting, setNewMeeting] = useState({
    title: "",
    startTime: "09:00",
    duration: 30,
    recurring: "",
    importance: "medium",
    prepItems: [],
    prepNotes: "",
    reminderMinutes: [60],
    date: new Date().toISOString().split("T")[0],
  });
  const [newPrepItem, setNewPrepItem] = useState("");

  // AI Prep Generation state
  const [showAiPrepDialog, setShowAiPrepDialog] = useState(false);
  const [aiPrepContext, setAiPrepContext] = useState("");
  const [aiPrepAttendees, setAiPrepAttendees] = useState("");
  const [aiPrepRole, setAiPrepRole] = useState("");
  const [aiPrepOutcome, setAiPrepOutcome] = useState("");
  const [generatingPrep, setGeneratingPrep] = useState(false);
  const [generatedPrep, setGeneratedPrep] = useState(null);

  // Fetch upcoming meetings only when the user flips into upcoming mode.
  useEffect(() => {
    const fetchUpcoming = async () => {
      try {
        const res = await axios.get(`${API}/meetings/upcoming?days=7`, { withCredentials: true });
        setUpcomingMeetings(res.data || []);
      } catch (error) {
        console.error("Error fetching upcoming meetings:", error);
      }
    };
    if (showUpcoming) {
      fetchUpcoming();
    }
  }, [showUpcoming, meetings]);

  // Generate AI Prep Notes
  const generateAiPrep = async () => {
    if (!newMeeting.title || !aiPrepContext) {
      toast.error("Please enter meeting title and context");
      return;
    }

    setGeneratingPrep(true);
    try {
      const response = await axios.post(
        `${API}/meetings/generate-prep`,
        {
          meeting_title: newMeeting.title,
          meeting_context: aiPrepContext,
          attendees: aiPrepAttendees || null,
          your_role: aiPrepRole || null,
          desired_outcome: aiPrepOutcome || null,
        },
        { withCredentials: true }
      );

      setGeneratedPrep(response.data.prep_notes);
      toast.success("Prep notes generated!");
    } catch (error) {
      console.error("Error generating prep:", error);
      toast.error("Failed to generate prep notes");
    } finally {
      setGeneratingPrep(false);
    }
  };

  const applyGeneratedPrep = () => {
    if (generatedPrep) {
      setNewMeeting({
        ...newMeeting,
        prepNotes: generatedPrep,
      });
      setShowAiPrepDialog(false);
      setGeneratedPrep(null);
      toast.success("Prep notes applied!");
    }
  };

  const handleAddMeeting = async () => {
    const [hours, minutes] = newMeeting.startTime.split(":").map(Number);
    const start = hours + minutes / 60;
    const end = start + newMeeting.duration / 60;

    const meetingData = {
      title: newMeeting.title || `Meeting`,
      start_time: start,
      end_time: end,
      recurring: newMeeting.recurring || null,
      date: newMeeting.date,
      importance: newMeeting.importance,
      prep_items: newMeeting.prepItems.length > 0 ? newMeeting.prepItems : null,
      prep_notes: newMeeting.prepNotes || null,
      reminder_minutes: newMeeting.reminderMinutes.length > 0 ? newMeeting.reminderMinutes : [60],
    };

    if (onAddMeeting) {
      await onAddMeeting(meetingData);
    } else {
      setMeetings([
        ...meetings,
        {
          id: Date.now(),
          ...meetingData,
        },
      ]);
      toast.success("Meeting added!");
    }

    // Reset form
    setNewMeeting({
      title: "",
      startTime: "09:00",
      duration: 30,
      recurring: "",
      importance: "medium",
      prepItems: [],
      prepNotes: "",
      reminderMinutes: [60],
      date: new Date().toISOString().split("T")[0],
    });
    setShowDialog(false);
  };

  const addPrepItem = () => {
    if (newPrepItem.trim()) {
      setNewMeeting({
        ...newMeeting,
        prepItems: [...newMeeting.prepItems, newPrepItem.trim()],
      });
      setNewPrepItem("");
    }
  };

  const removePrepItem = (index) => {
    setNewMeeting({
      ...newMeeting,
      prepItems: newMeeting.prepItems.filter((_, i) => i !== index),
    });
  };

  const toggleReminder = (minutes) => {
    const current = newMeeting.reminderMinutes;
    if (current.includes(minutes)) {
      setNewMeeting({
        ...newMeeting,
        reminderMinutes: current.filter((m) => m !== minutes),
      });
    } else {
      setNewMeeting({
        ...newMeeting,
        reminderMinutes: [...current, minutes].sort((a, b) => a - b),
      });
    }
  };

  const handleRemoveMeeting = (meetingId) => {
    if (onDeleteMeeting) {
      onDeleteMeeting(meetingId);
    } else {
      setMeetings(meetings.filter((m) => m.id !== meetingId));
    }
  };

  const getImportanceColor = (importance) => {
    switch (importance) {
      case "high":
        return "bg-red-500/20 border-red-500/30 text-red-400";
      case "medium":
        return "bg-violet-500/10 border-violet-500/20";
      case "low":
        return "bg-zinc-500/10 border-zinc-500/20";
      default:
        return "bg-violet-500/10 border-violet-500/20";
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + "T00:00:00");
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (dateStr === today.toISOString().split("T")[0]) return "Today";
    if (dateStr === tomorrow.toISOString().split("T")[0]) return "Tomorrow";
    return date.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
  };

  return (
    <Card className="bento-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Calendar className="w-5 h-5 text-violet-400" />
            Meetings
          </CardTitle>
          <div className="flex gap-1">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setShowUpcoming(!showUpcoming)}
              className={showUpcoming ? "bg-violet-500/20" : ""}
            >
              <Clock className="w-4 h-4 mr-1" />
              {showUpcoming ? "Today" : "Upcoming"}
            </Button>
            <Dialog open={showDialog} onOpenChange={setShowDialog}>
              <DialogTrigger asChild>
                <Button size="sm" variant="outline" data-testid="add-meeting-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Calendar className="w-5 h-5 text-violet-400" />
                    Schedule Meeting
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {/* Title */}
                  <div className="space-y-2">
                    <Label>Meeting Name</Label>
                    <Input
                      value={newMeeting.title}
                      onChange={(e) => setNewMeeting({ ...newMeeting, title: e.target.value })}
                      placeholder="e.g., Staff Huddle, Supervision"
                      data-testid="meeting-title"
                    />
                  </div>

                  {/* Date */}
                  <div className="space-y-2">
                    <Label>Date</Label>
                    <Input
                      type="date"
                      value={newMeeting.date}
                      onChange={(e) => setNewMeeting({ ...newMeeting, date: e.target.value })}
                      min={new Date().toISOString().split("T")[0]}
                      data-testid="meeting-date"
                    />
                  </div>

                  {/* Time & Duration */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Start Time</Label>
                      <Input
                        type="time"
                        value={newMeeting.startTime}
                        onChange={(e) => setNewMeeting({ ...newMeeting, startTime: e.target.value })}
                        data-testid="meeting-start"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Duration</Label>
                      <div className="flex gap-2">
                        {[15, 30, 45, 60].map((d) => (
                          <Button
                            key={d}
                            type="button"
                            size="sm"
                            variant={newMeeting.duration === d ? "default" : "outline"}
                            onClick={() => setNewMeeting({ ...newMeeting, duration: d })}
                          >
                            {d}m
                          </Button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Importance */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Importance
                    </Label>
                    <div className="flex gap-2">
                      {[
                        { label: "Low", value: "low", color: "bg-zinc-500/20" },
                        { label: "Medium", value: "medium", color: "bg-violet-500/20" },
                        { label: "High", value: "high", color: "bg-red-500/20" },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          size="sm"
                          className={newMeeting.importance === opt.value ? opt.color : ""}
                          variant={newMeeting.importance === opt.value ? "default" : "outline"}
                          onClick={() => setNewMeeting({ ...newMeeting, importance: opt.value })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Repeat */}
                  <div className="space-y-2">
                    <Label>Repeat</Label>
                    <div className="flex gap-2">
                      {[
                        { label: "Once", value: "" },
                        { label: "Daily", value: "daily" },
                        { label: "Weekly", value: "weekly" },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          size="sm"
                          variant={newMeeting.recurring === opt.value ? "default" : "outline"}
                          onClick={() => setNewMeeting({ ...newMeeting, recurring: opt.value })}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Reminders */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <Bell className="w-4 h-4" />
                      Remind me before
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {[
                        { label: "15 min", value: 15 },
                        { label: "30 min", value: 30 },
                        { label: "1 hour", value: 60 },
                        { label: "2 hours", value: 120 },
                        { label: "1 day", value: 1440 },
                      ].map((opt) => (
                        <Button
                          key={opt.value}
                          type="button"
                          size="sm"
                          variant={newMeeting.reminderMinutes.includes(opt.value) ? "default" : "outline"}
                          onClick={() => toggleReminder(opt.value)}
                          className={newMeeting.reminderMinutes.includes(opt.value) ? "bg-amber-500/20" : ""}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                  </div>

                  {/* Prep Items */}
                  <div className="space-y-2">
                    <Label className="flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Prep Items (what to prepare before)
                    </Label>
                    <div className="flex gap-2">
                      <Input
                        value={newPrepItem}
                        onChange={(e) => setNewPrepItem(e.target.value)}
                        placeholder="e.g., Review incident reports"
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addPrepItem())}
                      />
                      <Button type="button" variant="outline" onClick={addPrepItem}>
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                    {newMeeting.prepItems.length > 0 && (
                      <div className="space-y-1 mt-2">
                        {newMeeting.prepItems.map((item, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm p-2 bg-zinc-800/50 rounded-lg">
                            <CheckCircle2 className="w-3 h-3 text-emerald-400" />
                            <span className="flex-1">{item}</span>
                            <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removePrepItem(idx)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Prep Notes */}
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>Prep Notes</Label>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setShowAiPrepDialog(true)}
                        className="h-7 text-xs"
                      >
                        <Sparkles className="w-3 h-3 mr-1" />
                        Generate with AI
                      </Button>
                    </div>
                    <Textarea
                      value={newMeeting.prepNotes}
                      onChange={(e) => setNewMeeting({ ...newMeeting, prepNotes: e.target.value })}
                      placeholder="Any additional notes for meeting prep..."
                      className="min-h-[60px]"
                    />
                  </div>

                  <Button onClick={handleAddMeeting} className="w-full" data-testid="save-meeting">
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Meeting
                  </Button>
                </div>
              </DialogContent>
            </Dialog>

            {/* AI Prep Generation Dialog */}
            <Dialog open={showAiPrepDialog} onOpenChange={setShowAiPrepDialog}>
              <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-2">
                    <Sparkles className="w-5 h-5 text-violet-400" />
                    AI Meeting Prep Generator
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  {!generatedPrep ? (
                    <>
                      <div className="p-3 rounded-lg bg-violet-500/10 border border-violet-500/20 text-sm">
                        <p className="text-violet-300">
                          Tell me about your meeting and I&apos;ll generate strategic questions, talking points, and prep notes.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label>Meeting Context *</Label>
                        <Textarea
                          value={aiPrepContext}
                          onChange={(e) => setAiPrepContext(e.target.value)}
                          placeholder="What is this meeting about? What will be discussed?"
                          className="min-h-[80px]"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Attendees (optional)</Label>
                        <Input
                          value={aiPrepAttendees}
                          onChange={(e) => setAiPrepAttendees(e.target.value)}
                          placeholder="e.g., Medical Director, CNO, Quality team"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Your Role (optional)</Label>
                        <Input
                          value={aiPrepRole}
                          onChange={(e) => setAiPrepRole(e.target.value)}
                          placeholder="e.g., Presenting data, seeking approval, brainstorming"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>Desired Outcome (optional)</Label>
                        <Input
                          value={aiPrepOutcome}
                          onChange={(e) => setAiPrepOutcome(e.target.value)}
                          placeholder="e.g., Get buy-in for new protocol, identify action items"
                        />
                      </div>

                      <Button
                        onClick={generateAiPrep}
                        disabled={generatingPrep || !aiPrepContext}
                        className="w-full bg-violet-600 hover:bg-violet-700"
                      >
                        {generatingPrep ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Generating Prep Notes...
                          </>
                        ) : (
                          <>
                            <Sparkles className="w-4 h-4 mr-2" />
                            Generate Prep Notes
                          </>
                        )}
                      </Button>
                    </>
                  ) : (
                    <>
                      <div className="p-4 rounded-lg bg-zinc-900 border border-zinc-800 max-h-[400px] overflow-y-auto">
                        <div className="prose prose-invert prose-sm max-w-none whitespace-pre-wrap">{generatedPrep}</div>
                      </div>

                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => {
                            setGeneratedPrep(null);
                          }}
                          className="flex-1"
                        >
                          Regenerate
                        </Button>
                        <Button onClick={applyGeneratedPrep} className="flex-1 bg-emerald-600 hover:bg-emerald-700">
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Use These Notes
                        </Button>
                      </div>
                    </>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {showUpcoming ? (
          // Upcoming meetings view
          <div className="space-y-3">
            {upcomingMeetings.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No upcoming meetings in the next 7 days.</p>
            ) : (
              Object.entries(
                upcomingMeetings.reduce((acc, mtg) => {
                  const date = mtg.date;
                  if (!acc[date]) acc[date] = [];
                  acc[date].push(mtg);
                  return acc;
                }, {})
              ).map(([date, dateMeetings]) => (
                <div key={date}>
                  <p className="text-xs font-medium text-muted-foreground mb-1">{formatDate(date)}</p>
                  <div className="space-y-1">
                    {dateMeetings.map((meeting) => (
                      <div
                        key={`${meeting.meeting_id}-${date}`}
                        className={`flex items-center justify-between p-2 rounded-lg border ${getImportanceColor(meeting.importance)}`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium">{meeting.title}</p>
                            {meeting.importance === "high" && <AlertTriangle className="w-3 h-3 text-red-400" />}
                            {meeting.prep_needed && (
                              <Badge variant="outline" className="text-xs bg-amber-500/20 border-amber-500/30">
                                Prep
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground">
                            {formatHour(meeting.start_time)} - {formatHour(meeting.end_time)}
                            {meeting.recurring && ` • ${meeting.recurring}`}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))
            )}
          </div>
        ) : // Today's meetings view
        meetings.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No meetings today. Add your meetings to optimize your day!
          </p>
        ) : (
          <div className="space-y-2">
            {meetings
              .sort((a, b) => (a.start_time || a.start) - (b.start_time || b.start))
              .map((meeting) => (
                <div
                  key={meeting.meeting_id || meeting.id}
                  className={`flex items-center justify-between p-2 rounded-lg border ${getImportanceColor(meeting.importance)}`}
                >
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium">{meeting.title}</p>
                      {meeting.importance === "high" && <AlertTriangle className="w-3 h-3 text-red-400" />}
                      {meeting.recurring && (
                        <Badge variant="outline" className="text-xs">
                          {meeting.recurring}
                        </Badge>
                      )}
                      {(meeting.prep_items?.length > 0 || meeting.prep_notes) && (
                        <Badge variant="outline" className="text-xs bg-amber-500/20 border-amber-500/30">
                          <CheckCircle2 className="w-3 h-3 mr-1" />
                          Prep
                        </Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {formatHour(meeting.start_time || meeting.start)} - {formatHour(meeting.end_time || meeting.end)}
                    </p>
                    {meeting.prep_items?.length > 0 && (
                      <div className="mt-1 text-xs text-amber-400">
                        {meeting.prep_items.slice(0, 2).join(" • ")}
                        {meeting.prep_items.length > 2 && ` +${meeting.prep_items.length - 2} more`}
                      </div>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-muted-foreground hover:text-red-400"
                    onClick={() => handleRemoveMeeting(meeting.meeting_id || meeting.id)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default MeetingPlanner;
